import { createTheme, responsiveFontSizes, alpha } from '@mui/material/styles';

/*
 * FIG Marketplace Theme — Swan K-12 (swank12.com)
 * ------------------------------------------------
 * Sourced from the AIOS structured theme field for project `fig-marketplace`
 * (GET /api/aios/projects/fig-marketplace/theme). This is the canonical FIG
 * brand identity: the turquoise / green / orange trio that blends with both
 * the Swan Solutions and FIG logos.
 *
 *   Teal   #1ebec8   (primary)
 *   Green  #86c65a   (secondary)
 *   Orange #f79153   (accent / warning)
 *   Red    #fa4747   (error)
 *   Ink    #36454f   (text)
 *   Style: glassmorphic, professional, subtle gradients.
 *
 * Font: Poppins (loaded in public/index.html).
 */

// --- FIG brand tokens ------------------------------------------------------
export const fig = {
    teal: '#1ebec8',
    tealDark: '#159aa2',
    tealLight: '#5fd6de',
    green: '#86c65a',
    greenDark: '#6ba844',
    greenLight: '#a6db83',
    orange: '#f79153',
    red: '#fa4747',
    ink: '#36454f',
    inkSoft: '#5f6b73',
    bg: '#f9f9f9',
    surface: '#ffffff',
    gradient: 'linear-gradient(90deg, #1ebec8 0%, #86c65a 100%)',
    gradientHero: 'linear-gradient(120deg, #0f8b93 0%, #1ebec8 45%, #86c65a 100%)',
    gradientAccent: 'linear-gradient(90deg, #1ebec8 0%, #f79153 100%)',
};

const rawTheme = createTheme({
    palette: {
        mode: 'light',
        common: { black: '#000000', white: '#FFFFFF' },
        primary: {
            main: fig.teal,
            light: fig.tealLight,
            dark: fig.tealDark,
            contrastText: '#FFFFFF',
        },
        secondary: {
            main: fig.green,
            light: fig.greenLight,
            dark: fig.greenDark,
            contrastText: '#FFFFFF',
        },
        // Orange accent from the FIG trio — used for highlight CTAs / emphasis.
        accent: {
            main: fig.orange,
            light: '#ffb07d',
            dark: '#d9762f',
            contrastText: '#FFFFFF',
        },
        success: { main: fig.green, light: fig.greenLight, dark: fig.greenDark, contrastText: '#FFFFFF' },
        error: { main: fig.red, light: '#ff7a7a', dark: '#c62828', contrastText: '#FFFFFF' },
        warning: { main: fig.orange, light: '#ffb07d', dark: '#d9762f', contrastText: '#FFFFFF' },
        info: { main: fig.teal, light: fig.tealLight, dark: fig.tealDark, contrastText: '#FFFFFF' },
        grey: {
            50: '#fafafa', 100: '#f5f5f5', 200: '#eeeeee', 300: '#e0e0e0', 400: '#bdbdbd',
            500: '#9e9e9e', 600: '#757575', 700: '#616161', 800: '#424242', 900: '#212121',
        },
        background: { default: fig.bg, paper: fig.surface },
        text: {
            primary: fig.ink,
            secondary: fig.inkSoft,
            disabled: alpha(fig.ink, 0.38),
        },
        divider: alpha(fig.ink, 0.1),
        action: {
            active: fig.teal,
            hover: alpha(fig.teal, 0.06),
            selected: alpha(fig.teal, 0.1),
            disabled: alpha(fig.ink, 0.26),
            disabledBackground: alpha(fig.ink, 0.12),
            focus: alpha(fig.teal, 0.12),
        },
        gradients: {
            primary: fig.gradient,
            hero: fig.gradientHero,
            accent: fig.gradientAccent,
        },
    },
    typography: {
        fontFamily: '"Poppins", "Helvetica", "Arial", sans-serif',
        fontSize: 16,
        fontWeightLight: 300,
        fontWeightRegular: 400,
        fontWeightMedium: 500,
        fontWeightBold: 600,
        h1: { fontWeight: 700, fontSize: '3.4rem', lineHeight: 1.12, letterSpacing: '-0.03em' },
        h2: { fontWeight: 700, fontSize: '2.6rem', lineHeight: 1.16, letterSpacing: '-0.02em' },
        h3: { fontWeight: 600, fontSize: '2.05rem', lineHeight: 1.22, letterSpacing: '-0.01em' },
        h4: { fontWeight: 600, fontSize: '1.65rem', lineHeight: 1.3 },
        h5: { fontWeight: 600, fontSize: '1.3rem', lineHeight: 1.4 },
        h6: { fontWeight: 600, fontSize: '1.1rem', lineHeight: 1.5 },
        subtitle1: { fontWeight: 500, fontSize: '1.07rem', lineHeight: 1.6 },
        subtitle2: { fontWeight: 600, fontSize: '0.95rem', lineHeight: 1.55 },
        body1: { fontWeight: 400, fontSize: '1.05rem', lineHeight: 1.62 },
        body2: { fontWeight: 400, fontSize: '0.95rem', lineHeight: 1.58 },
        button: { fontWeight: 600, fontSize: '1rem', textTransform: 'none', letterSpacing: '0.01em' },
        caption: { fontWeight: 500, fontSize: '0.82rem', lineHeight: 1.5 },
        overline: { fontWeight: 600, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.12em' },
    },
    shape: { borderRadius: 12 },
    breakpoints: { values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 } },
    spacing: 8,
    components: {
        MuiCssBaseline: {
            styleOverrides: `
                body {
                    background-color: ${fig.bg};
                    color: ${fig.ink};
                    -webkit-font-smoothing: antialiased;
                    -moz-osx-font-smoothing: grayscale;
                }
                /* Haptic feedback utility classes (press/release micro-scale) */
                .haptic-press { transform: scale(0.97); transition: transform 60ms ease-in-out; }
                .haptic-release { transform: scale(1); transition: transform 120ms ease-out; }
                .fig-glow { filter: drop-shadow(0 0 8px ${alpha(fig.teal, 0.55)}); }
            `,
        },
        MuiButton: {
            defaultProps: { disableElevation: false },
            styleOverrides: {
                root: {
                    borderRadius: 999,
                    textTransform: 'none',
                    fontWeight: 600,
                    transition: 'transform 160ms ease, box-shadow 160ms ease, background 200ms ease',
                    '&:active': { transform: 'scale(0.97)' }, // haptic press
                },
                containedPrimary: {
                    background: fig.gradient,
                    color: '#FFFFFF',
                    boxShadow: `0 6px 18px ${alpha(fig.teal, 0.32)}`,
                    '&:hover': {
                        background: 'linear-gradient(90deg, #159aa2 0%, #6ba844 100%)',
                        boxShadow: `0 8px 22px ${alpha(fig.teal, 0.4)}`,
                    },
                },
                containedSecondary: {
                    background: 'linear-gradient(90deg, #86c65a 0%, #1ebec8 100%)',
                    color: '#FFFFFF',
                    boxShadow: `0 6px 18px ${alpha(fig.green, 0.32)}`,
                },
                outlinedPrimary: {
                    borderColor: fig.teal,
                    color: fig.tealDark,
                    '&:hover': { backgroundColor: alpha(fig.teal, 0.06), borderColor: fig.tealDark },
                },
                textPrimary: { color: fig.tealDark },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    color: fig.teal,
                    transition: 'color 160ms ease, background 160ms ease, transform 160ms ease',
                    '&:active': { transform: 'scale(0.92)' },
                    '&:hover': { color: fig.tealDark, backgroundColor: alpha(fig.teal, 0.06) },
                },
            },
        },
        MuiAppBar: {
            defaultProps: { elevation: 0, color: 'transparent' },
            styleOverrides: {
                root: {
                    // Glassmorphic top bar
                    background: alpha('#ffffff', 0.72),
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    borderBottom: `1px solid ${alpha(fig.ink, 0.08)}`,
                    color: fig.ink,
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 16,
                    border: `1px solid ${alpha(fig.ink, 0.06)}`,
                    boxShadow: `0 8px 30px ${alpha(fig.ink, 0.08)}`,
                    backgroundImage: 'none',
                    transition: 'transform 200ms ease, box-shadow 200ms ease',
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                rounded: { borderRadius: 16 },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: { fontWeight: 600, borderRadius: 999 },
                colorPrimary: { backgroundColor: alpha(fig.teal, 0.12), color: fig.tealDark },
                colorSecondary: { backgroundColor: alpha(fig.green, 0.14), color: fig.greenDark },
            },
        },
        MuiTextField: {
            defaultProps: { variant: 'outlined' },
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 12,
                        '& fieldset': { borderColor: alpha(fig.ink, 0.2) },
                        '&:hover fieldset': { borderColor: fig.teal },
                        '&.Mui-focused fieldset': {
                            borderColor: fig.teal,
                            boxShadow: `0 0 0 3px ${alpha(fig.teal, 0.18)}`,
                        },
                    },
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: { backgroundColor: fig.ink, color: '#fff', fontSize: '0.8rem', borderRadius: 8 },
                arrow: { color: fig.ink },
            },
        },
        MuiLink: {
            defaultProps: { underline: 'hover' },
            styleOverrides: { root: { color: fig.tealDark, fontWeight: 500 } },
        },
    },
    // Custom extensions consumed by scenes for consistent motion / glass.
    custom: {
        haptics: {
            whileHover: { scale: 1.03 },
            whileTap: { scale: 0.96 },
            transition: { type: 'spring', stiffness: 330, damping: 16 },
        },
        glass: {
            background: alpha('#ffffff', 0.65),
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: `1px solid ${alpha('#ffffff', 0.5)}`,
            boxShadow: `0 8px 32px ${alpha(fig.ink, 0.1)}`,
        },
        gradients: {
            primary: fig.gradient,
            hero: fig.gradientHero,
            accent: fig.gradientAccent,
        },
    },
});

const theme = responsiveFontSizes(rawTheme);

export default theme;
